import { useTranslations } from '@vocab/react';
import {
  IconCopy,
  IconEdit,
  IconRefresh,
  Columns,
  Column,
  Text,
  useResponsiveValue,
} from 'braid-design-system';
import type React from 'react';

import type { JobListItemFragment } from 'src/graphql/__generated__';

import { useConfig } from '../../../hooks/useConfig';
import { IconButtonLink } from '../IconButtonLink/IconButtonLink';

import translations from './.vocab';
import { JobActionMenu } from './JobActionMenu';
import { JobActionsLoader } from './JobActionsLoader';
import { useGetJobActionStatus } from './jobActionStatus';
import { createCopyLink, createEditLink, createRefreshLink } from './links';

interface JobActionsProps {
  job: JobListItemFragment;
  loading?: boolean;
}

export const JobActions: React.FC<React.PropsWithChildren<JobActionsProps>> = ({
  job,
  loading = false,
}) => {
  const { t } = useTranslations(translations);
  const { urlResolver } = useConfig();
  const responsiveValue = useResponsiveValue();
  const jobActionStatus = useGetJobActionStatus(job);
  const isJobBlocked = job.status === 'BLOCKED';

  const columnWidth = responsiveValue({
    mobile: 'content',
    desktop: 'content',
  }) as undefined | 'content';

  if (loading) {
    return <JobActionsLoader />;
  }

  return (
    <Columns space={['medium', 'small']} align="right">
      <Column width={columnWidth}>
        <IconButtonLink
          id={`${job.id}-refresh`}
          tooltip={<Text>{t('Refresh your ad')}</Text>}
          href={urlResolver({ path: createRefreshLink(job) })}
          hoverBackground="formAccentSoft"
          eventName="job_action_pressed"
          eventAttributes={{
            linkAction: 'refresh',
            jobId: job.id,
          }}
          icon={
            <IconRefresh titleId={`${job.id}-refresh`} title={t('Refresh')} />
          }
          disabled={
            jobActionStatus.refreshActionStatus.inactive || isJobBlocked
          }
          disabledTooltip={
            jobActionStatus.refreshActionStatus.inactiveDescription ? (
              <Text>
                {jobActionStatus.refreshActionStatus.inactiveDescription}
              </Text>
            ) : undefined
          }
        />
      </Column>
      <Column width={columnWidth}>
        <IconButtonLink
          id={`${job.id}-edit`}
          tooltip={<Text>{t('Edit the job details')}</Text>}
          href={urlResolver({ path: createEditLink(job) })}
          hoverBackground="formAccentSoft"
          eventName="job_action_pressed"
          eventAttributes={{ linkAction: 'edit', jobId: job.id }}
          icon={<IconEdit titleId={`${job.id}-edit`} title={t('Edit')} />}
          disabled={jobActionStatus.editActionStatus.inactive || isJobBlocked}
          disabledTooltip={
            jobActionStatus.editActionStatus.inactiveDescription ? (
              <Text>
                {jobActionStatus.editActionStatus.inactiveDescription}
              </Text>
            ) : undefined
          }
        />
      </Column>
      <Column width={columnWidth}>
        <IconButtonLink
          id={`${job.id}-copy`}
          tooltip={
            <Text>
              {t('Create a new job by copying the details of this job')}
            </Text>
          }
          href={urlResolver({ path: createCopyLink(job) })}
          hoverBackground="formAccentSoft"
          eventName="job_action_pressed"
          eventAttributes={{ linkAction: 'copy', jobId: job.id }}
          icon={<IconCopy titleId={`${job.id}-copy`} title={t('Copy')} />}
          disabled={jobActionStatus.copyActionStatus.inactive || isJobBlocked}
          disabledTooltip={
            jobActionStatus.copyActionStatus.inactiveDescription ? (
              <Text>
                {jobActionStatus.copyActionStatus.inactiveDescription}
              </Text>
            ) : undefined
          }
        />
      </Column>
      <Column width={columnWidth}>
        <JobActionMenu
          disabled={isJobBlocked}
          job={job}
          jobActionStatus={jobActionStatus}
        />
      </Column>
    </Columns>
  );
};
